'use strict';

//***********************************************************************************
//***********************************************************************************
//******     CN-Map    **************************************************************
//******     Copyright(C) 2019-2020 EnerBIM                        ******************
//***********************************************************************************
//***********************************************************************************
//***********************************************************************************
//***********************************************************************************
//**** Material
//***********************************************************************************
//***********************************************************************************
import { cn_material_type, CN_MATERIALS_TYPES } from './cn_material_type';
import { cn_element } from './cn_element';

export class cn_material extends cn_element {

    /**
     * @param {any} parent
     * @param {cn_material_type} material_type
     * @param {number} thickness
     */
    constructor(
        parent,
        material_type,
        thickness = 0.1,
    ) {
        super(parent);
        this.class_name = 'cn_material';
        this.material_type = material_type;
        this._thickness = thickness;
    }

    //***********************************************************************************

    get name() {
        return this.material_type.name;
    }

    get code() {
        return this.material_type.code;
    }

    get type() {
        return this.material_type.type;
    }

    get conductivity() {
        return this.material_type.conductivity;
    }

    get density() {
        return this.material_type.density;
    }

    get specific_heat() {
        return this.material_type.specific_heat;
    }

    get color() {
        return this.material_type.color;
    }

    /**
     * Returns material type thickness, or material thickness if material type has no fixed thickness.
     * @returns {number}
     */
    get thickness() {
        return this.material_type.thickness || this._thickness;
    }

    /**
     * Sets material thickness.
     * @param {number} thickness
     */
    set thickness(thickness) {
        this._thickness = thickness;
    }

    //***********************************************************************************
    /**
     * Returns a default material by code
     * @param {import('./cn_building').cn_building} building
     * @param {string} code
     * @param {number} thickness
     * @return {cn_material}
     */
    static material_by_code(building, code, thickness = 0.1) {
        const corresponding_material = CN_MATERIALS_TYPES.find(mat => mat.code === code);
        if (corresponding_material) {
            const type = corresponding_material.clone();
            return new cn_material(building, type, thickness);
        } else {
            console.error(`Could not find material '${code}'`);
            return null;
        }

    }

    static get_material_name_by_code(code) {
        const corresponding_material = CN_MATERIALS_TYPES.find(mat => mat.code === code);
        return corresponding_material ? corresponding_material.name : 'Inconnu';
    }

    //***********************************************************************************
    //**** Clone
    //***********************************************************************************
    /**
     * @returns {cn_material}
     */
    clone() {
        // NB : material type must not be cloned !
        return new cn_material(this.parent, this.material_type, this._thickness);
    }

    //***********************************************************************************
    //**** serialize
    //***********************************************************************************

    serialize() {
        return this._serialize(false)
    }

    serialize_building_agnostic() {
        return this._serialize(true)
    }

    /**
     *
     * @param {boolean} building_agnostic Must be false for building serialization (only ID is serialized for expert materials), but true for librairies (all material_type is serialized, to be building-agnostic)
     * @returns {{}}
     */
    _serialize(building_agnostic) {
        const json = this.serialize_element();
        if (this.material_type.custom) {
            // user custom
            if (building_agnostic) {
                // => building agnostic => full type is serialized, without ID (to avoid potential ID conflicts between buildings)
                json.material_type = this.material_type.serialize();
                delete json.material_type.ID;
            } else {
                // => building dependant => only ID is serialized
                json.material_type = { ID: this.material_type.ID };
            }
        } else {
            // building default => only code is serialized
            json.material_type = { code: this.material_type.code };
        }
        json.thickness = this._thickness;
        return json;
    }

    /**
     * @param {any} json
     * @param {import('./cn_building').cn_building} building
     * @returns {cn_material}
     */
    static unserialize(json, building) {
        let material_type;
        if (typeof (json.code) == 'string') {
            // old version, for backward compatibility
            material_type = cn_material_type.unserialize(json);
            material_type.unserialize_element(json);
        } else if (typeof (json.material_type) == 'object') {
            // current version
            if (json.material_type.custom) {
                // user custom + building agnostic => full type is serialized => unserialize it
                material_type = cn_material_type.unserialize(json.material_type)
            } else if (json.material_type.ID) {
                // user custom + building dependant => only ID is serialized => get material_type from building
                material_type = building.get_element_type(json.material_type.ID);
            } else if (json.material_type.code) {
                // building default => only code is serialized => get generic material by code
                material_type = cn_material_type.material_type_by_code(json.material_type.code);
            }
        } else {
            return null;
        }
        if (!material_type) return null;
        // @ts-ignore
        return new cn_material(building, material_type, json.thickness);
    }

    //***********************************************************************************
    //**** returns label
    //***********************************************************************************
    get_label() {
        return this.name + ' ' + (this.thickness * 100).toFixed(0) + 'cm';
    }

    //***********************************************************************************
    //**** draw
    //***********************************************************************************
    draw_material(origin, width, height, scale) {
        return this.material_type.draw_material(origin, width, height, scale);
    }
}

